<template>
  <div :class="blockClasses">
    <div class="white-block__inner" :class="{ 'white-block__inner--resizable': isResizable }">
      <Heading v-if="header" :class="headerClasses" :tag="HEADINGS.h4"
        ><slot name="beforeHeader" /><span v-html="header"></span><slot name="afterHeader"
      /></Heading>
      <LineBreakResolver v-if="subheader" :class="subheaderClasses" tag="p" :text="subheader" />
      <slot />
    </div>
  </div>
</template>

<script>
import { populateClassModifiers } from '@/helpers/style'
import Heading, { HEADINGS } from '@/components/Heading/Heading.vue'
import LineBreakResolver from '@/components/LineBreakResolver.vue'

export const BLOCK_MODIFIERS = {
  hasTable: 'has-table',
}

export default {
  name: 'WhiteBlock',
  components: {
    Heading,
    LineBreakResolver,
  },

  props: {
    header: {
      type: String,
    },
    headerClass: {
      type: [Array, Object, String],
    },
    subheader: {
      type: String,
    },
    subheaderClass: {
      type: [Array, Object, String],
    },
    modifiers: {
      type: Array,
      validator: array =>
        array.every(modifier => Object.values(BLOCK_MODIFIERS).includes(modifier)),
      default: () => [],
    },
    isResizable: {
      type: Boolean,
      default: true,
    },
    hasGreyBackground: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      HEADINGS,
    }
  },

  computed: {
    blockClasses() {
      const modifiers = [...this.modifiers]
      if (this.hasGreyBackground) modifiers.push('grey-bg')
      return populateClassModifiers('white-block', modifiers)
    },

    headerClasses() {
      const modifiers = []
      if (!this.subheader) modifiers.push('no-subheader')
      return [
        populateClassModifiers('white-block__header', [...this.modifiers, ...modifiers]),
        ...(this.headerClass && Array.isArray(this.headerClass)
          ? this.headerClass
          : [this.headerClass]),
      ]
    },

    subheaderClasses() {
      return [
        populateClassModifiers('white-block__subheader', this.modifiers),
        ...(this.subheaderClass && Array.isArray(this.subheaderClass)
          ? this.subheaderClass
          : [this.subheaderClass]),
      ]
    },
  },
}
</script>

<style lang="scss">
// Omit "scoped" attribute to avoid specificity conflicts when overwriting those generic styles
@import './white-block-settings';

.white-block {
  @include white-block-padding;
  background-color: $color-white;
  box-shadow: $box-shadow;
  border-radius: $border-radius;
  width: 100%;

  & + & {
    margin-top: $white-block-margin-top;
  }

  &--grey-bg {
    background-color: $color-light-grey-4;
    border: 1px solid darken($color-light-grey-4, 5);
    box-shadow: none;
  }

  &__inner {
    &--resizable {
      overflow: hidden;
    }
  }

  // to overwrite margins from Heading
  & &__header {
    cursor: default;

    &--has-table {
      margin-left: $table-padding-x;
    }

    &--no-subheader {
      @include white-block-margin-bottom;
    }
  }

  &__subheader {
    @include white-block-margin-bottom;

    &--has-table {
      margin-left: $table-padding-x;
    }
  }
}
</style>
